import type { AABB2 } from "./aabb2Types";
import { Vec2PolyLine2ContainmentClass, classifyContainmentVec2PolyLine2 } from "./containment";
import { intersectPolyLine2PolyLine2 } from "./intersect";
import { CurveCurveRangeIntersection } from "./intersectLine2Line2";
import { polyLine2FromAABB2 } from "./polyLine2";
import { polyLine2New } from "./polyLine2Types";

/**
 * Return the part of the line that's inside an AABB.
 * @param x0
 * @param y0
 * @param x1
 * @param y1
 * @param aabb AABB to clip by.
 * @returns The clipped line or undefined if the line is fully outside the AABB.
 */
export function line2ClipToAABB(x0: number, y0: number, x1: number, y1: number, aabb: AABB2): number[] | undefined {
  if (
    (x0 < aabb.minX && x1 < aabb.minX) ||
    (x0 > aabb.maxX && x1 > aabb.maxX) ||
    (y0 < aabb.minY && y1 < aabb.minY) ||
    (y0 > aabb.maxY && y1 > aabb.maxY)
  ) {
    // The segment is fully outside the AABB.
    return undefined;
  }
  if (
    x0 < aabb.minX ||
    x1 < aabb.minX ||
    x0 > aabb.maxX ||
    x1 > aabb.maxX ||
    y0 < aabb.minY ||
    y1 < aabb.minY ||
    y0 > aabb.maxY ||
    y1 > aabb.maxY
  ) {
    // The segment can be fully or partially outside the AABB.
    const aabbPolyline = polyLine2FromAABB2(aabb);
    const ints = intersectPolyLine2PolyLine2(aabbPolyline, polyLine2New([x0, y0, x1, y1]));
    if (ints.length === 0) {
      return undefined;
    }
    if (ints[0].int instanceof CurveCurveRangeIntersection) {
      return [ints[0].int.startx, ints[0].int.starty, ints[0].int.endx, ints[0].int.endy];
    }
    if (ints.length > 1) {
      if (ints[1].int instanceof CurveCurveRangeIntersection) {
        return [ints[1].int.startx, ints[1].int.starty, ints[1].int.endx, ints[1].int.endy];
      }
      if (ints.length > 2 && ints[2].int instanceof CurveCurveRangeIntersection) {
        return [ints[2].int.startx, ints[2].int.starty, ints[2].int.endx, ints[2].int.endy];
      }
      return [ints[0].int.x, ints[0].int.y, ints[1].int.x, ints[1].int.y];
    }

    // x0, y0 will be the point that's inside the AABB.
    if (classifyContainmentVec2PolyLine2(x1, y1, aabbPolyline) === Vec2PolyLine2ContainmentClass.INSIDE) {
      x0 = x1;
      y0 = y1;
    }
    // and the other point is the intersection point.
    return [x0, y0, ints[0].int.x, ints[0].int.y];
  }
  // Line is fully inside the aabb.
  return [x0, y0, x1, y1];
}
